const { getElement, getElements } = window.BAO.Utils
class Cart {
  constructor (drawer) {
    this.drawer = drawer
  }

  async updateCartSections () {
    const sections = (() => {
      if (getElement('push-cart')) {
        return window.BAO.sectionRenderer.pushCartSections
      }

      if (getElement('cart-items')) {
        return window.BAO.sectionRenderer.cartSections
      }

      return []
    })()
    const fetchedSections = await window.BAO.sectionRenderer.fetch(
      sections.map(s => s.section),
    )

    Object.keys(fetchedSections).forEach(key => {
      sections.find(s => s.section === key).html = fetchedSections[key]
    })

    window.BAO.sectionRenderer.renderMultiple(sections)
  }

  async updateProperties () {
    await window.BAO.Cart.updateCartAttributes({
      [window.BAO.Gifting.PROPERTY_KEYS.GIFT_MESSAGE]: this.giftMessage,
    })
  }

  async removeProperties () {
    await window.BAO.Cart.updateCartAttributes({
      [window.BAO.Gifting.PROPERTY_KEYS.GIFT_MESSAGE]: '',
    })
  }

  get giftMessage () {
    return this.drawer.giftMessage
  }

  get loading () {
    return this.drawer.els.apply.element.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.drawer.els.apply.element.setAttribute('aria-busy', val.toString())
  }

  findNewItems () {
    return {
      extras: this.#findNewExtrasItems(),
    }
  }

  #findNewExtrasItems () {
    return this.drawer.activeExtras
  }
}

class CartAdder extends Cart {
  async add () {
    this.loading = true

    const itemsToAdd = []

    const newItems = this.findNewItems()
    if (newItems.extras.length > 0) {
      newItems.extras.forEach(el => {
        itemsToAdd.push({
          id: el.dataset.variantId,
          product_id: el.dataset.productId,
          quantity:
            parseInt(
              getElement('.qty-Adjuster_Input', { context: el })?.value,
            ) || 1,
          properties: {
            [window.BAO.Gifting.PROPERTY_KEYS.TYPES]: el.dataset.type,
            [window.BAO.Gifting.PROPERTY_KEYS.EXTRA_PRODUCT]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.HIDE_FROM_CART]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.FREE_AFTER_1]:
              el.dataset.free === 'true',
          },
        })
      })
    }

    await this.updateProperties()

    await window.BAO.Cart.addItem(itemsToAdd)

    await this.updateCartSections()

    this.loading = false
  }
}

class CartUpdater extends Cart {
  async update () {
    this.drawer.els.apply.element.setAttribute('aria-busy', 'true')

    const state = await window.BAO.Cart.cartGet()
    const currentItems = this.findCurrentItems(state.items)
    const newItems = this.findNewItems()

    this.drawer.log('Items', { currentItems, newItems })

    const removedItems = this.findRemovedItems(currentItems, newItems)
    const addedItems = this.findAddedItems(currentItems, newItems)

    this.drawer.log('Changes', { addedItems, removedItems })

    await this.updateProperties()

    if (removedItems.length > 0) {
      await window.BAO.Cart.removeItems(removedItems)
    }

    if (addedItems.length > 0) {
      await window.BAO.Cart.addItem(addedItems)
    }

    await this.updateCartSections()

    this.drawer.els.apply.element.setAttribute('aria-busy', 'false')
  }

  async remove ({ busyElement = this.drawer.els.reset.element } = {}) {
    busyElement.setAttribute('aria-busy', 'true')

    const state = await window.BAO.Cart.cartGet()
    const currentItems = this.findCurrentItems(state.items)

    this.drawer.log('remove', { state, currentItems })

    await this.updateProperties()

    const itemsToRemove = []

    if (currentItems.extras.length > 0) {
      currentItems.extras.forEach(item => itemsToRemove.push(item.key))
    }

    this.drawer.log('Items to remove', { itemsToRemove })

    if (itemsToRemove.length > 0) {
      await window.BAO.Cart.removeItems(itemsToRemove)
    }

    await this.updateCartSections()

    busyElement.setAttribute('aria-busy', 'false')
  }

  async removeOldTypes ({ busyElement = this.drawer.els.apply.element } = {}) {
    busyElement.setAttribute('aria-busy', 'true')

    const state = await window.BAO.Cart.cartGet()
    if (!state?.items || state.items.length === 0) {
      busyElement.setAttribute('aria-busy', 'false')
      return
    }

    const currentItems = this.findCurrentItems(state.items)
    this.drawer.log('RemoveOldTypes - remove', { state, currentItems })

    const currentTypes = this.drawer.currentTypesFlattened

    const itemsToRemove = []
    if (currentItems.extras.length > 0) {
      currentItems.extras.forEach(item => {
        const types = (
          item.properties[window.BAO.Gifting.PROPERTY_KEYS.TYPES] || ''
        )
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)

        if (!types.some(type => currentTypes.includes(type))) {
          itemsToRemove.push(item.key)
        }
      })
    }

    let removedGiftMessage = false
    const giftMessageTypeStr = this.drawer.els?.messageWrapper?.element?.dataset
      ?.type
    const giftMessageTypes = giftMessageTypeStr
      ? giftMessageTypeStr
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      : []
    if (
      giftMessageTypes.length > 0 &&
      !giftMessageTypes.some(item => currentTypes.includes(item)) &&
      state.attributes?.[window.BAO.Gifting.PROPERTY_KEYS.GIFT_MESSAGE]
    ) {
      await this.removeProperties()
      removedGiftMessage = true
    }

    this.drawer.log('RemoveOldTypes - Items to remove', { itemsToRemove })

    if (itemsToRemove.length > 0) {
      await window.BAO.Cart.removeItems(itemsToRemove)
    }

    // Only re-render cart sections when we actually changed gifting (removed extras or gift message).
    // Otherwise we would wipe the "add to wishlist" CTA that cart-item shows after removing a product.
    const didChangeGifting = itemsToRemove.length > 0 || removedGiftMessage
    if (didChangeGifting) {
      await this.updateCartSections()
    }

    busyElement.setAttribute('aria-busy', 'false')
  }

  /**
   * @param {{ extras: ShopifyLineItem[] }} currentItems
   * @param {{ extras: HTMLDivElement[] }} newItems
   *
   * @returns {PotentialCartItem[]}
   */
  findAddedItems (currentItems, newItems) {
    const addedItems = []

    newItems.extras.forEach(el => {
      const quantity = parseInt(
        getElement('.qty-Adjuster_Input', { context: el })?.value,
      )
      const exists = currentItems.extras.find(
        extra =>
          Number(el.dataset.variantId) === extra.variant_id &&
          quantity === extra.quantity,
      )

      if (!exists) {
        addedItems.push({
          id: el.dataset.variantId,
          quantity: quantity || 1,
          properties: {
            [window.BAO.Gifting.PROPERTY_KEYS.TYPES]: el.dataset.type,
            [window.BAO.Gifting.PROPERTY_KEYS.EXTRA_PRODUCT]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.HIDE_FROM_CART]: 'true',
            [window.BAO.Gifting.PROPERTY_KEYS.FREE_AFTER_1]:
              el.dataset.free === 'true',
          },
        })
      }
    })

    return addedItems
  }

  /**
   * @param {{ extras: ShopifyLineItem[] }} currentItems
   * @param {{ extras: HTMLDivElement[] }} newItems
   *
   * @returns {string[]}
   */
  findRemovedItems (currentItems, newItems) {
    const removedItems = []

    currentItems.extras.forEach(extra => {
      const stillExists = newItems.extras.find(el => {
        const quantity = parseInt(
          getElement('.qty-Adjuster_Input', { context: el })?.value,
        )
        return (
          Number(el.dataset.variantId) === extra.variant_id &&
          quantity === extra.quantity
        )
      })

      if (!stillExists) {
        removedItems.push(extra.key)
      }
    })

    return removedItems
  }

  findCurrentItems (items) {
    return {
      extras: this.#findCurrentExtrasItems(items),
    }
  }

  #findCurrentExtrasItems (items) {
    return items.filter(
      item =>
        item?.properties[window.BAO.Gifting.PROPERTY_KEYS.EXTRA_PRODUCT] ===
        'true',
    )
  }
}

class GiftDrawer extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['reset', 'apply']
  }

  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    this.activator = null
    this._removingOldTypes = false
  }

  setupListeners () {
    super.setupListeners()

    this.onExtraClick = this.onExtraClick.bind(this)
    this.onResetClick = this.onResetClick.bind(this)
    this.onApplyClick = this.onApplyClick.bind(this)
    this.onCartUpdate = this.onCartUpdate.bind(this)

    this.listeners.add(this, 'click', this.onExtraClick)
    this.listeners.add(this.els.reset.element, 'click', this.onResetClick)
    this.listeners.add(this.els.apply.element, 'click', this.onApplyClick)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartUpdate,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      this.onCartUpdate,
    )

    if (this.els?.message?.element) {
      this.onMessageChange = this.onMessageChange.bind(this)

      this.listeners.add(
        this.els.message.element,
        'input',
        this.onMessageChange,
      )
    }
  }

  open () {
    window.drawers.activeDrawerKey = this.drawerKey
    this.updateVisibility()
  }

  close () {
    window.drawers.activeDrawerKey = this.cartDrawerKey
  }

  async onCartUpdate () {
    if (this._removingOldTypes) return
    this._removingOldTypes = true
    try {
      // Defer visibility so it runs after DOM updates (e.g. cart-item adding psh-CartItem-removed).
      await new Promise(resolve => setTimeout(resolve, 0))
      this.updateVisibility()
      const cartUpdater = new CartUpdater(this)
      await cartUpdater.removeOldTypes()
    } finally {
      this._removingOldTypes = false
    }
  }

  updateVisibility () {
    const rawTypes = this.currentTypes
    const allTypesInCart = rawTypes
      .flatMap(t => (t || '').split(',').map(s => s.trim()))
      .filter(Boolean)

    // Types that allow extras (Gift Bag, Gift Wrap, etc.) come from extra buttons' data-type.
    // Hide extras only when the cart has no type that allows any extra (i.e. only "gift message only" products).
    const typesThatAllowExtras = [
      ...new Set(
        this.extras.flatMap(el =>
          (el.dataset.type || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
        ),
      ),
    ]
    const onlyGiftMessageInCart =
      allTypesInCart.length > 0 &&
      !allTypesInCart.some(type => typesThatAllowExtras.includes(type))

    const extrasContainer = this.querySelector(
      '[data-gift-drawer-el="extrasContainer"]',
    )
    if (extrasContainer) {
      extrasContainer.ariaHidden = onlyGiftMessageInCart
    }

    this.extras.forEach(element => {
      const types = (element.dataset.type || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      element.parentElement.ariaHidden = !types.some(type =>
        allTypesInCart.includes(type),
      )
    })

    this.giftMessageWrapper.forEach(element => {
      const types = (element.dataset.type || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      element.ariaHidden = !types.some(type => allTypesInCart.includes(type))
    })
  }

  onExtraClick (event) {
    if (!this.els.extra?.selector) {
      return
    }

    const target = event.target.closest(this.els.extra.selector)
    if (!target) return

    const quantitySelector = event.target.closest('quantity-adjuster')
    if (quantitySelector) return

    target.setAttribute(
      'aria-selected',
      (target.getAttribute('aria-selected') !== 'true').toString(),
    )
  }

  async reset ({ busyElement = this.els.reset.element } = {}) {
    this.activator.extrasIds = []
    this.activator.extrasIdsFree = []
    this.activator.extrasIdsTypes = []
    this.activator.els.added.element.textContent = ''
    this.activator.hasGifting = false

    this.giftMessage = ''

    this.extras.forEach(el => el.setAttribute('aria-selected', 'false'))

    if (this.activator.applyAsUpdate) {
      const cartUpdater = new CartUpdater(this)

      await cartUpdater.remove({ busyElement })
    } else {
      const cartUpdater = new CartUpdater(this)

      await cartUpdater.updateProperties({ busyElement })
    }

    this.activator = null

    this.close()
  }

  async apply () {
    this.activator.hasGifting = true

    this.log('onApplyClick', { activator: this.activator })

    this.activator.els.added.element.textContent = this.getGiftingMessage()
    this.activator.els.added.element.value = this.getGiftingMessageValue()
    this.activator.extrasIds = this.activeExtras.map(el =>
      Number(el.dataset.variantId),
    )
    this.activator.extrasIdsFree = this.activeExtras.map(el => el.dataset.free)
    this.activator.extrasIdsTypes = this.activeExtras.map(el => el.dataset.type)
    this.activator.giftMessage = this.giftMessage

    if (this.activator.applyAsAdd) {
      const cartAdder = new CartAdder(this)

      await cartAdder.add()
    } else if (this.activator.applyAsUpdate) {
      const cartUpdater = new CartUpdater(this)

      await cartUpdater.update()
    }

    this.activator = null

    this.close()
  }

  onApplyClick () {
    if (this.giftMessage.length === 0 && this.activeExtras.length === 0) {
      this.reset({ busyElement: this.els.apply.element })
      return
    }

    this.apply()
  }

  onResetClick () {
    this.reset()
  }

  onMessageChange (event) {
    this.els.characterCount.element.textContent = `${event.target.value.length}/${this.els.characterCount.element.dataset.maxCharacters}`
  }

  getGiftingMessageValue () {
    const items = [...this.activeExtras].filter(Boolean)

    const parts = []

    if (items.length > 0) {
      const itemsString = items
        .map(item =>
          item.querySelector(this.els.title.selector)?.dataset.value?.trim(),
        )
        .filter(Boolean)
        .join(', ')

      parts.push(itemsString)
    }

    if (this.giftMessage) {
      parts.push('Free Gift Message Added')
    }

    return parts.join(' / ')
  }

  getGiftingMessage () {
    const items = [...this.activeExtras].filter(Boolean)

    const parts = []

    if (items.length > 0) {
      const itemsString = items
        .map(item =>
          item.querySelector(this.els.title.selector)?.textContent?.trim(),
        )
        .filter(Boolean)
        .join(', ')

      parts.push(itemsString)
    }

    if (this.giftMessage) {
      parts.push('Free Gift Message Added')
    }

    return parts.join(' / ')
  }

  get currentTypes () {
    // Scope to push-cart so we always read the cart drawer's items (they may not be
    // in the same DOM tree when the gift drawer is open).
    const pushCart = getElement('push-cart')
    const context = pushCart || document
    const items = getElements('cart-item:not(.psh-CartItem-removed)', {
      context,
    })
    return items.map(el => el.dataset.type)
  }

  get currentTypesFlattened () {
    return this.currentTypes
      .flatMap(t => (t || '').split(',').map(s => s.trim()))
      .filter(Boolean)
  }

  get drawerKey () {
    return this.closest('site-drawer').getAttribute('key')
  }

  get cartDrawerKey () {
    return 'cart'
  }

  get extras () {
    return this.els.extra?.elements ?? []
  }

  get giftMessageWrapper () {
    return this.els.messageWrapper?.elements ?? []
  }

  get activeExtras () {
    return this.extras.filter(
      extra => extra.getAttribute('aria-selected') === 'true',
    )
  }

  get giftMessage () {
    return this.els?.message?.element?.value
  }

  set giftMessage (val) {
    if (!this.els?.message) return

    this.els.message.element.value = val

    this.els.message.element.dispatchEvent(new Event('input'))
  }

  get productId () {
    return this.dataset.productId ? Number(this.dataset.productId) : null
  }

  set productId (val) {
    this.dataset.productId = val
  }

  get variantId () {
    return this.dataset.variantId ? Number(this.dataset.variantId) : null
  }

  set variantId (val) {
    this.dataset.variantId = val
  }
}

if (!customElements.get('gift-drawer')) {
  customElements.define('gift-drawer', GiftDrawer)
}
